import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';

import { RouterLink } from './RouterLink';
import Text from './Text';

type Props = {
  label: string;
  variant?: 'primary' | 'text';
  colorScheme?: 'accent' | 'black';
  size?: 'large' | 'small';
  disabled?: boolean;
  iconLeft?: (props: { size: number; color: string }) => React.ReactNode;
  iconRight?: (props: { size: number; color: string }) => React.ReactNode;
  to?: string;
  onClick?: () => void;
  type?: React.ComponentProps<typeof html.button>['type'];
  style?: React.ComponentProps<typeof html.button>['style'];
};

export default function Button({
  label,
  variant = 'primary',
  size = 'large',
  colorScheme = 'accent',
  disabled,
  iconLeft,
  iconRight,
  to,
  onClick,
  type,
  style: styleProp,
}: Props) {
  const baseStyle = [
    Style.inline,
    Style.centerBoth,
    styles.base,
    variant === 'primary' &&
      (colorScheme === 'accent' ? styles.primary : styles.primaryBlack),
    variant === 'text' && styles.text,
    size === 'small' && styles.small,
    disabled &&
      variant === 'primary' &&
      (colorScheme === 'accent'
        ? styles.primaryDisabled
        : styles.primaryBlackDisabled),
    disabled && variant === 'text' && styles.textDisabled,
  ];

  const buttonStyle = styleProp
    ? [...baseStyle, ...(Array.isArray(styleProp) ? styleProp : [styleProp])]
    : baseStyle;

  // default accent color scheme
  let textColor: keyof typeof Color =
    variant === 'primary' ? 'neutral0' : 'accent';

  if (colorScheme === 'black') {
    textColor = variant === 'primary' ? 'neutral0' : 'neutral60';
  }

  if (disabled && variant === 'text') {
    textColor = 'neutral40';
  }

  const content = (
    <>
      {iconLeft && (
        <html.span
          style={[Style.row, Style.centerBoth, Style.shrink0, styles.icon]}
        >
          {iconLeft({ size: 24, color: Color[textColor] })}
        </html.span>
      )}
      <Text typeface="body.medium" color={textColor}>
        {label}
      </Text>
      {iconRight && (
        <html.span
          style={[Style.row, Style.centerBoth, Style.shrink0, styles.icon]}
        >
          {iconRight({ size: 24, color: Color[textColor] })}
        </html.span>
      )}
    </>
  );

  if (to && !disabled) {
    return (
      <RouterLink to={to} onClick={onClick}>
        <html.div style={buttonStyle}>{content}</html.div>
      </RouterLink>
    );
  }

  return (
    <html.button
      style={buttonStyle}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      type={type}
    >
      {content}
    </html.button>
  );
}

const styles = css.create({
  base: {
    gap: 6,
    borderRadius: 100,
    borderWidth: 0,
    fontFamily: 'Inter, sans-serif',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 1.4,
    cursor: 'pointer',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    paddingInline: 16,
    paddingBlock: 10,
    height: 44,
    transition: 'background-color 0.15s',
  },
  primary: {
    backgroundColor: color.accent,
    color: '#fff',
    ':hover': {
      backgroundColor: '#a3292a',
    },
  },
  primaryBlack: {
    backgroundColor: color.neutral100,
    color: color.neutral0,
    ':hover': {
      backgroundColor: color.neutral80,
    },
  },
  text: {
    backgroundColor: 'transparent',
    color: color.accent,
  },
  small: {
    height: 40,
    paddingBlock: 8,
  },
  primaryDisabled: {
    backgroundColor: '#da8383',
    cursor: 'default',
    ':hover': {
      backgroundColor: '#da8383',
    },
  },
  primaryBlackDisabled: {
    backgroundColor: color.neutral40,
    cursor: 'default',
    ':hover': {
      backgroundColor: color.neutral40,
    },
  },
  textDisabled: {
    color: color.neutral40,
    cursor: 'default',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    width: 24,
    height: 24,
  },
});
